import React, { useEffect, useRef, useState } from "react";
import "./ProductShowcase.css";

const views = [
    {
        label: "The whole picture",
        image: "/review-demo/product-overview.webp",
        alt: "The Quicksight workspace, with a video, its transcript, and review comments together.",
        caption:
            "Your video, the words, and the conversation. Together in the actual Quicksight workspace.",
    },
    {
        label: "Follow every word",
        image: "/review-demo/product-transcript.webp",
        alt: "An uploaded video in Quicksight with its transcript open alongside it.",
        caption:
            "The transcript alongside an uploaded video. Read along, then click a word to jump to its moment.",
    },
    {
        label: "Feedback in context",
        image: "/review-demo/product-comments.webp",
        alt: "The Quicksight comments panel showing timestamped feedback beside the video.",
        caption:
            "Timestamped notes beside the video they refer to. A real review, with the context close at hand.",
    },
];

function ExpandIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M8 3H3v5m13-5h5v5M3 16v5h5m13-5v5h-5M3 3l6 6m12-6-6 6M3 21l6-6m12 6-6-6" />
        </svg>
    );
}

export default function ProductShowcase() {
    const [selected, setSelected] = useState(0);
    const [expanded, setExpanded] = useState(false);
    const [naturalSize, setNaturalSize] = useState(false);
    const [failedImages, setFailedImages] = useState<number[]>([]);
    const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
    const dialogRef = useRef<HTMLDialogElement>(null);
    const closeRef = useRef<HTMLButtonElement>(null);
    const openerRef = useRef<HTMLElement | null>(null);
    const view = views[selected];

    useEffect(() => {
        if (!expanded) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [expanded]);

    const handleTabKey = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
        let next: number;
        switch (event.key) {
            case "ArrowRight":
                next = (index + 1) % views.length;
                break;
            case "ArrowLeft":
                next = (index + views.length - 1) % views.length;
                break;
            case "Home":
                next = 0;
                break;
            case "End":
                next = views.length - 1;
                break;
            default:
                return;
        }
        event.preventDefault();
        setSelected(next);
        tabRefs.current[next]?.focus();
    };

    const openImage = () => {
        const dialog = dialogRef.current;
        if (!dialog || dialog.open) return;
        openerRef.current = document.activeElement as HTMLElement | null;
        setNaturalSize(false);
        dialog.showModal();
        setExpanded(true);
        closeRef.current?.focus();
    };

    const closeImage = () => dialogRef.current?.close();
    const restoreFocus = () => {
        setExpanded(false);
        setNaturalSize(false);
        openerRef.current?.focus();
    };

    return (
        <section id="playground" className="qr-showcase" aria-label="Explore Quicksight">
            <div className="qr-showcase-tabs" role="tablist" aria-label="Product screenshots">
                {views.map((item, index) => (
                    <button
                        key={item.image}
                        ref={element => {
                            tabRefs.current[index] = element;
                        }}
                        type="button"
                        role="tab"
                        id={`qr-showcase-tab-${index}`}
                        aria-controls={`qr-showcase-panel-${index}`}
                        aria-selected={selected === index}
                        tabIndex={selected === index ? 0 : -1}
                        onClick={() => setSelected(index)}
                        onKeyDown={event => handleTabKey(event, index)}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            {views.map((item, index) => (
                <div
                    key={item.image}
                    role="tabpanel"
                    id={`qr-showcase-panel-${index}`}
                    aria-labelledby={`qr-showcase-tab-${index}`}
                    hidden={selected !== index}
                >
                    {selected === index && (
                        <figure className="qr-showcase-figure">
                            <div className="qr-showcase-image">
                                {failedImages.includes(index) ? (
                                    <p role="status">
                                        This screenshot could not load. Choose another view or
                                        reload the page.
                                    </p>
                                ) : (
                                    <img
                                        src={item.image}
                                        alt={item.alt}
                                        decoding="async"
                                        onError={() =>
                                            setFailedImages(current => [...current, index])
                                        }
                                    />
                                )}
                            </div>
                            <figcaption className="qr-showcase-caption">
                                <p>{item.caption}</p>
                                <button
                                    type="button"
                                    onClick={openImage}
                                    disabled={failedImages.includes(index)}
                                    aria-haspopup="dialog"
                                >
                                    <ExpandIcon />
                                    View full size
                                </button>
                            </figcaption>
                        </figure>
                    )}
                </div>
            ))}

            <dialog
                ref={dialogRef}
                className="qr-showcase-dialog"
                aria-labelledby="qr-showcase-dialog-title"
                aria-describedby="qr-showcase-dialog-description"
                onClose={restoreFocus}
                onClick={event => {
                    if (event.target !== event.currentTarget) return;
                    const bounds = event.currentTarget.getBoundingClientRect();
                    if (
                        event.clientX < bounds.left ||
                        event.clientX > bounds.right ||
                        event.clientY < bounds.top ||
                        event.clientY > bounds.bottom
                    )
                        closeImage();
                }}
            >
                <div className="qr-showcase-dialog-header">
                    <div>
                        <h2 id="qr-showcase-dialog-title">{view.label}</h2>
                        <p id="qr-showcase-dialog-description">
                            {naturalSize
                                ? "Full-resolution screenshot. Scroll to see every detail."
                                : "A screenshot from the actual Quicksight workspace."}
                        </p>
                    </div>
                    <div className="qr-showcase-dialog-actions">
                        <button
                            type="button"
                            aria-pressed={naturalSize}
                            onClick={() => setNaturalSize(current => !current)}
                        >
                            {naturalSize ? "Fit to screen" : "Zoom in"}
                        </button>
                        <button
                            ref={closeRef}
                            type="button"
                            className="qr-showcase-close"
                            onClick={closeImage}
                            aria-label="Close full-size screenshot"
                        >
                            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                                <path d="m6 6 12 12M6 18 18 6" />
                            </svg>
                        </button>
                    </div>
                </div>
                <div
                    className={`qr-showcase-dialog-image${naturalSize ? " is-natural-size" : ""}`}
                    tabIndex={naturalSize ? 0 : -1}
                    aria-label={naturalSize ? "Scrollable full-resolution screenshot" : undefined}
                >
                    {expanded && <img src={view.image} alt={view.alt} />}
                </div>
            </dialog>
        </section>
    );
}
